import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Components
import {
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Drawer,
  Field,
  Input,
  Spinner,
  Button,
  makeStyles,
  shorthands,
  tokens,
  useRestoreFocusSource,
  DrawerFooter,
  Checkbox,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import DataValidationMessageBar from "../../../shared/components/DataValidationMessageBar";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import { useNotify } from "../../../shared/components/SuperToast";

// Styles
import "../../../styles/animations.css";

// Business logic
import { useSelectedMailboxesContext } from "../context/SelectedMailboxContext";
import { useMailboxQuery } from "../hooks/useMailboxQueries";
import { useCreateMailbox, useUpdateMailbox } from "../hooks/useMailboxMutations";
import { mailboxSchema } from "@shared/schemas";
import { useItemValidation } from "../../../shared/hooks/useItemValidation";

const useStyles = makeStyles({
  root: {
    overflow: "visible",
    display: "flex",
    backgroundColor: "#fff",
    zIndex: "1000",
  },
  content: {
    flex: "1",
    padding: "16px",
    display: "grid",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gridRowGap: tokens.spacingVerticalXXL,
    gridAutoRows: "max-content",
  },
  field: {
    display: "grid",
    gridRowGap: "4px",
    margin: "6px 0",
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    ...shorthands.gap("1rem"),
  },
  sectionHeader: {
    fontSize: "14px",
    fontWeight: "600",
    marginTop: "16px",
    marginBottom: "8px",
    color: tokens.colorNeutralForeground1,
  },
});

function MailboxDrawer({ isOpen, setIsOpen, newMailbox }) {
  const restoreFocusSourceAttributes = useRestoreFocusSource();
  const { t } = useTranslation();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [editingMailbox, setEditingMailbox] = useState(null);
  const { value: selectedMailboxes } = useSelectedMailboxesContext();
  const { notify } = useNotify();

  // Validation
  const [validationErrors, setValidationErrors] = useState({});
  const validateMailbox = useItemValidation(mailboxSchema.$defs.mailbox);

  // Errors
  const [errors, setErrors] = useState([]);

  const public_id = selectedMailboxes?.[0]?.public_id ?? null;

  // 1. Query and mutations
  const { data, error, isError, isLoading, isSuccess } = useMailboxQuery(public_id, isOpen);

  const {
    mutate: createMailbox,
    isPending: isCreating,
    error: errorCreating,
    reset: resetCreating,
  } = useCreateMailbox(
    "mailboxes",
    (data) => {
      notify(t("item.messages.created", { name: data.name }), "success");
      handleClose();
    },
    null,
    false,
  );

  const {
    mutate: updateMailbox,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useUpdateMailbox(
    "mailboxes",
    (data) => {
      notify(t("item.messages.saved", { name: data.name }), "success");
      handleClose();
    },
    null,
  );

  // 2. Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (errorCreating) {
      newErrors.push({ message: errorCreating?.message || t("generic.error"), type: "error", details: errorCreating });
    }
    if (errorUpdating) {
      newErrors.push({ message: errorUpdating?.message || t("generic.error"), type: "error", details: errorUpdating });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, errorCreating, errorUpdating, t]);

  useEffect(() => {
    if (!isOpen) return;

    if (newMailbox) {
      const initialMailbox = {
        id: 0,
        name: "",
        email_address: "",
        protocol: "imaps",
        active: 1,
        incoming_host: "",
        incoming_port: 993,
        incoming_secure: 1,
        outgoing_host: "",
        outgoing_port: 587,
        outgoing_secure: 1,
      };
      setEditingMailbox(initialMailbox);
      setValidationErrors(validateMailbox(initialMailbox));
    } else if (data && isSuccess) setEditingMailbox(data);
    else setEditingMailbox(null);
  }, [isOpen, data, newMailbox]);

  useEffect(() => {
    if (isOpen && isError) {
      notify(error.message, "error");
      handleClose();
    }
  }, [isOpen, isError]);

  // 3. Handlers
  const handleClose = () => {
    queryClient.removeQueries({ queryKey: ["mailbox", public_id] });
    setEditingMailbox(null);
    setValidationErrors({});
    setErrors([]);
    resetCreating();
    resetUpdating();
    setIsOpen(false);
  };

  const handleDrawHeaderTitleButtonOnClick = () => setIsOpen(false);

  const handleOpenChange = (_, data) => {
    if (data.type !== "backdropClick") setIsOpen(data.open);
  };

  const updateField = (field, value) => {
    const updated = { ...editingMailbox, [field]: value };
    setEditingMailbox(updated);
    setValidationErrors(validateMailbox(updated));
  };

  const protocolOptions = [
    { key: "imaps", text: "IMAP/S" },
    { key: "pops", text: "POP3/S" },
    { key: "m365", text: "Microsoft 365" },
    { key: "gmail", text: "Gmail" },
  ];

  return (
    <div className={styles.root}>
      <Drawer
        {...restoreFocusSourceAttributes}
        type="overlay"
        position="end"
        separator
        size="medium"
        open={isOpen}
        onOpenChange={handleOpenChange}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={handleDrawHeaderTitleButtonOnClick}
              />
            }
          >
            {newMailbox ? t("mailboxes.new") : (editingMailbox?.name ?? "")}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <hr />
        {!newMailbox && isLoading && (
          <div
            className="blur-overlay"
            style={{
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(6px)",
              backgroundColor: "rgba(255, 255, 255, 0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Spinner size="medium" />
          </div>
        )}
        <DrawerBody>
          <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />

          {/* Basic Information */}
          <Field
            className={styles.field}
            label={t("generic.name")}
            validationState={validationErrors.name ? "error" : undefined}
            required
          >
            <Input
              id="name"
              maxLength="50"
              value={editingMailbox?.name ?? ""}
              onChange={(e) => updateField("name", e.target.value)}
            />
          </Field>

          <Field
            className={styles.field}
            label={t("generic.email")}
            validationState={validationErrors.email_address ? "error" : undefined}
            required
          >
            <Input
              id="email_address"
              type="email"
              maxLength="255"
              value={editingMailbox?.email_address ?? ""}
              onChange={(e) => updateField("email_address", e.target.value)}
            />
          </Field>

          <div style={{ display: "flex", gap: "10px" }}>
            <Field
              className={styles.field}
              label={t("generic.protocol")}
              validationState={validationErrors.protocol ? "error" : undefined}
              style={{ flex: 1 }}
              required
            >
              <Dropdown
                value={protocolOptions.find((o) => o.key === editingMailbox?.protocol)?.text || "IMAP/S"}
                selectedOptions={[editingMailbox?.protocol || "imaps"]}
                onOptionSelect={(_, data) => updateField("protocol", data.optionValue)}
              >
                {protocolOptions.map((option) => (
                  <Option key={option.key} value={option.key} disabled={option.key !== "imaps"}>
                    {option.text}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <Field className={styles.field} style={{ display: "flex", alignItems: "flex-end", paddingBottom: "8px" }}>
              <Checkbox
                label="Enabled"
                checked={editingMailbox?.enabled === 1}
                onChange={(e, data) => updateField("enabled", data.checked ? 1 : 0)}
              />
            </Field>
          </div>

          {/* Incoming Server Settings */}
          <div className={styles.sectionHeader}>{t("nounPhrases.incomingServer")}</div>

          <Field
            className={styles.field}
            label={t("generic.host")}
            validationState={validationErrors.incoming_host ? "error" : undefined}
          >
            <Input
              id="incoming_host"
              maxLength="255"
              value={editingMailbox?.incoming_host ?? ""}
              onChange={(e) => updateField("incoming_host", e.target.value)}
            />
          </Field>

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: "0 0 120px" }}>
              <Field
                className={styles.field}
                label={t("generic.port")}
                validationState={validationErrors.incoming_port ? "error" : undefined}
              >
                <Input
                  id="incoming_port"
                  type="number"
                  value={editingMailbox?.incoming_port?.toString() ?? ""}
                  onChange={(e) => updateField("incoming_port", parseInt(e.target.value) || null)}
                />
              </Field>
            </div>
            <div style={{ flex: "1", display: "flex", alignItems: "flex-end", paddingBottom: "8px" }}>
              <Checkbox
                label="Secure (SSL/TLS)"
                checked={editingMailbox?.incoming_secure === 1}
                onChange={(e, data) => updateField("incoming_secure", data.checked ? 1 : 0)}
              />
            </div>
          </div>

          <Field className={styles.field} label={t("generic.username")}>
            <Input
              id="incoming_username"
              maxLength="255"
              value={editingMailbox?.incoming_username ?? ""}
              onChange={(e) => updateField("incoming_username", e.target.value)}
            />
          </Field>

          <Field className={styles.field} label={t("generic.password")}>
            <Input
              id="incoming_password"
              type="password"
              maxLength="255"
              placeholder={t("nounPhrases.changePassword")}
              value={editingMailbox?.incoming_password ?? ""}
              onChange={(e) => updateField("incoming_password", e.target.value)}
            />
          </Field>
          <Field className={styles.field} label={t("mailboxes.folderImportedPath")}>
            <Input
              id="options.imported_folder"
              maxLength="255"
              value={editingMailbox?.options?.imported_folder ?? ""}
              onChange={(e) => updateField("options", { ...editingMailbox.options, imported_folder: e.target.value })}
            />
          </Field>

          {/* Outgoing Server Settings */}
          <div className={styles.sectionHeader}>{t("nounPhrases.outgoingServer")}</div>

          <Field
            className={styles.field}
            label={t("generic.host")}
            validationState={validationErrors.outgoing_host ? "error" : undefined}
          >
            <Input
              id="outgoing_host"
              maxLength="255"
              value={editingMailbox?.outgoing_host ?? ""}
              onChange={(e) => updateField("outgoing_host", e.target.value)}
            />
          </Field>

          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: "0 0 120px" }}>
              <Field
                className={styles.field}
                label={t("generic.port")}
                validationState={validationErrors.outgoing_port ? "error" : undefined}
              >
                <Input
                  id="outgoing_port"
                  type="number"
                  value={editingMailbox?.outgoing_port?.toString() ?? ""}
                  onChange={(e) => updateField("outgoing_port", parseInt(e.target.value) || null)}
                />
              </Field>
            </div>
            <div style={{ flex: "1", display: "flex", alignItems: "flex-end", paddingBottom: "8px" }}>
              <Checkbox
                label="Secure (SSL/TLS)"
                checked={editingMailbox?.outgoing_secure === 1}
                onChange={(e, data) => updateField("outgoing_secure", data.checked ? 1 : 0)}
              />
            </div>
          </div>

          <Field className={styles.field} label={t("generic.username")}>
            <Input
              id="outgoing_username"
              maxLength="255"
              value={editingMailbox?.outgoing_username ?? ""}
              onChange={(e) => updateField("outgoing_username", e.target.value)}
            />
          </Field>

          <Field className={styles.field} label={t("generic.password")}>
            <Input
              id="outgoing_password"
              type="password"
              maxLength="255"
              placeholder={t("nounPhrases.changePassword")}
              value={editingMailbox?.outgoing_password ?? ""}
              onChange={(e) => updateField("outgoing_password", e.target.value)}
            />
          </Field>
          <Field className={styles.field} label={t("mailboxes.folderSentPath")}>
            <Input
              id="options.sent_folder"
              maxLength="255"
              value={editingMailbox?.options?.sent_folder ?? ""}
              onChange={(e) => updateField("options", { ...editingMailbox.options, sent_folder: e.target.value })}
            />
          </Field>

          {validationErrors && Object.keys(validationErrors).length > 0 && (
            <DataValidationMessageBar validationErrors={validationErrors} />
          )}
        </DrawerBody>
        <DrawerFooter>
          <div className={styles.footerButtons}>
            <Button
              icon={isCreating || isUpdating ? <Spinner size="tiny" /> : undefined}
              appearance="primary"
              disabled={isCreating || isUpdating || Object.keys(validationErrors).length > 0}
              onClick={() =>
                newMailbox ? createMailbox(editingMailbox, handleClose) : updateMailbox(editingMailbox, handleClose)
              }
            >
              {newMailbox ? t("generic.create") : t("generic.update")}
            </Button>
            <Button appearance="secondary" onClick={handleClose}>
              {t("generic.cancel")}
            </Button>
          </div>
        </DrawerFooter>
      </Drawer>
    </div>
  );
}

export default MailboxDrawer;
